import { ImagePlus, Loader2 } from 'lucide-react';
import { forwardRef, useRef, useEffect, useState, useCallback } from 'react';
import { useTemplateRenderer } from '../../../shared/lib/useTemplateRenderer';
import { colorWithOpacity } from '../../../shared/lib';
import { normalizeHeadline } from '../../../shared/lib/text';

export type LogoPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

interface PostTemplateProps {
  backgroundColor: string;
  useGradient?: boolean;
  backgroundColorEnd?: string;
  backgroundAlpha?: number;
  backgroundAlphaEnd?: number;
  textColor: string;
  useTwoColors?: boolean;
  textColorSecondary?: string;
  fontFamily: string;
  fontUrl?: string;
  fontName?: string;
  fontSize?: number;
  logoUrl?: string;
  logoPosition?: LogoPosition;
  logoSize?: number;
  textLineSpacing?: number;
  imageUrl?: string;
  onImageClick?: () => void;
  text?: string;
  textUppercase?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  fontBold?: boolean;
  fontVariationSettings?: Record<string, number>;
  textShadowEnabled?: boolean;
  textShadowColor?: string;
  textShadowBlur?: number;
  textShadowWidth?: number;
  textSoftShadowEnabled?: boolean;
  textSoftShadowColor?: string;
  textSoftShadowBlur?: number;
  textSoftShadowOffsetX?: number;
  textSoftShadowOffsetY?: number;
  textSoftShadowOpacity?: number;
  textShadowOpacity?: number;
  showLine?: boolean;
  lineColor?: string;
  lineHeight?: number;
  linePosition?: 'top' | 'bottom';
  fitMode?: 'fill' | 'fit';
  /**
   * Высота холста по аспекту фото (как превью без шаблона) вместо фикс. 4:5.
   * Включаем в превью поста; редактор шаблонов остаётся на 4:5.
   */
  autoAspect?: boolean;
  postLayout?: 'bottom-bar' | 'top-card' | 'frame';
  accentColor?: string;
  cardColor?: string;
  cardInset?: number;
  bandHeight?: number;
  frameUrl?: string;
  frameTextX?: number;
  frameTextY?: number;
  frameTextWidth?: number;
  frameTextVAlign?: 'top' | 'center' | 'bottom';
  autoFontMax?: number;
  bandGrow?: boolean;
  /**
   * Рендер сообщает подобранный кегль и потолок плашки (preview-space px).
   * Живёт в ref и НЕ входит в deps эффекта: иначе каждый ререндер родителя
   * с новой стрелкой перезапускал бы рендер превью.
   */
  onResolvedFontSize?: (info: { fontSize: number; max: number }) => void;
}

export const PostTemplate = forwardRef<HTMLDivElement, PostTemplateProps>((props, ref) => {
  const {
    backgroundColor,
    useGradient = false,
    backgroundColorEnd,
    backgroundAlpha = 1,
    backgroundAlphaEnd = 1,
    textColor,
    useTwoColors = false,
    textColorSecondary,
    fontFamily,
    fontUrl,
    fontName,
    fontSize,
    logoUrl,
    logoPosition,
    logoSize,
    textLineSpacing,
    imageUrl,
    onImageClick,
    text: rawText = 'Пример текста поста',
    textUppercase = false,
    textAlign = 'center',
    fontBold = false,
    fontVariationSettings,
    textShadowEnabled = false,
    textShadowColor = '#000000',
    textShadowBlur = 4,
    textShadowWidth = 4,
    textSoftShadowEnabled = false,
    textSoftShadowColor = '#000000',
    textSoftShadowBlur = 8,
    textSoftShadowOffsetX = 2,
    textSoftShadowOffsetY = 4,
    textSoftShadowOpacity = 100,
    textShadowOpacity = 100,
    showLine = false,
    lineColor = '#000000',
    lineHeight = 2,
    linePosition = 'bottom',
    fitMode = 'fill',
    autoAspect = false,
    postLayout = 'bottom-bar',
    accentColor = '#FFD700',
    cardColor = '#FFFFFF',
    cardInset = 32,
    bandHeight = 0.22,
    frameUrl,
    frameTextX = 0.5,
    frameTextY = 0.12,
    frameTextWidth = 0.8,
    frameTextVAlign = 'top',
    autoFontMax,
    bandGrow,
    onResolvedFontSize,
  } = props;

  // Хвостовые Enter и пробелы не должны менять превью (отзыв 04.09) — канвас
  // нормализует так же внутри wrapTextWithBreaks, DOM-фолбэк повторяет.
  const text = normalizeHeadline(rawText);
  
  // Ширину меряем по корню — тому же элементу, что и публикация (templateRef).
  // Раньше ref висел на плашке внутри DOM-фолбэка: та размонтируется после первого
  // рендера, sourceWidth падал на 400 и превью расходилось с публикацией по scale.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const setRootRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
  }, [ref]);
  const onResolvedFontSizeRef = useRef(onResolvedFontSize);
  onResolvedFontSizeRef.current = onResolvedFontSize;
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isFirstRender, setIsFirstRender] = useState(true);
  
  const { render, isRendering } = useTemplateRenderer();

  useEffect(() => {
    const delay = isFirstRender ? 100 : 250;
    
    const timer = setTimeout(async () => {
      try {
        console.log('🎨 Rendering template preview...');
        
        let sourceWidth = 400;
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          sourceWidth = rect.width || 400;
        }
        
        const url = await render({
          backgroundColor,
          useGradient,
          backgroundColorEnd,
          backgroundAlpha,
          backgroundAlphaEnd,
          textColor,
          useTwoColors,
          textColorSecondary,
          fontFamily,
          fontUrl,
          fontName,
          fontSize,
          logoUrl,
          logoPosition,
          logoSize,
          textLineSpacing,
          imageUrl,
          text,
          textUppercase,
          textAlign,
          fontBold,
          fontVariationSettings,
          fitMode,
          fitCanvasToMedia: autoAspect,
          showLine,
          lineColor,
          lineHeight,
          linePosition,
          textShadowEnabled,
          textShadowColor,
          textShadowBlur,
          textShadowWidth,
          textSoftShadowEnabled,
          textSoftShadowColor,
          textSoftShadowBlur,
          textSoftShadowOffsetX,
          textSoftShadowOffsetY,
          textSoftShadowOpacity,
          textShadowOpacity,
          sourceWidth,
          postLayout,
          accentColor,
          cardColor,
          cardInset,
          bandHeight,
          frameUrl,
          frameTextX,
          frameTextY,
          frameTextWidth,
          frameTextVAlign,
          autoFontMax,
          bandGrow,
          onResolvedFontSize: (info) => onResolvedFontSizeRef.current?.(info),
        });

        setPreviewUrl(url);
        setIsFirstRender(false);

        console.log('✅ Template preview updated');
      } catch (error) {
        if (error instanceof Error && error.message !== 'Render cancelled') {
          console.error('❌ Failed to render template:', error);
        }
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [
    backgroundColor,
    useGradient,
    backgroundColorEnd,
    backgroundAlpha,
    backgroundAlphaEnd,
    textColor,
    useTwoColors,
    textColorSecondary,
    fontFamily,
    fontUrl,
    fontName,
    fontSize,
    logoUrl,
    logoPosition,
    logoSize,
    textLineSpacing,
    imageUrl,
    text,
    textUppercase,
    textAlign,
    fontBold,
    fontVariationSettings,
    fitMode,
    autoAspect,
    showLine,
    lineColor,
    lineHeight,
    linePosition,
    textShadowEnabled,
    textShadowColor,
    textShadowBlur,
    textShadowWidth,
    textSoftShadowEnabled,
    textSoftShadowColor,
    textSoftShadowBlur,
    textSoftShadowOffsetX,
    textSoftShadowOffsetY,
    textSoftShadowOpacity,
    textShadowOpacity,
    postLayout,
    accentColor,
    cardColor,
    cardInset,
    bandHeight,
    frameUrl,
    frameTextX,
    frameTextY,
    frameTextWidth,
    frameTextVAlign,
    autoFontMax,
    bandGrow,
    render,
    isFirstRender,
  ]);

  const hexToRgba = (hex: string, alpha: number): string => {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const getBackgroundStyle = (): string => {
    if (useGradient) {
      const startColor = hexToRgba(backgroundColor, backgroundAlpha);
      const endColor = hexToRgba(backgroundColorEnd || backgroundColor, backgroundAlphaEnd);
      return `linear-gradient(to top, ${startColor}, ${endColor})`;
    }
    return hexToRgba(backgroundColor, backgroundAlpha);
  };

  const getLogoPositionClasses = (position: LogoPosition = 'top-left'): string => {
    const positions = {
      'top-left': 'top-4 left-4',
      'top-center': 'top-4 left-1/2 -translate-x-1/2',
      'top-right': 'top-4 right-4',
      'bottom-left': 'bottom-4 left-4',
      'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
      'bottom-right': 'bottom-4 right-4',
    };
    return positions[position];
  };

  return (
    <div
      ref={setRootRef}
      data-template-export="true"
      className="rounded-lg overflow-hidden border relative"
      // autoAspect: до готовности рендера держим 4:5, дальше высоту задаёт сам PNG.
      style={autoAspect && previewUrl ? undefined : { aspectRatio: '4/5' }}
    >
      {previewUrl ? (
        <div className={autoAspect ? 'relative w-full' : 'relative w-full h-full'}>
          <img
            src={previewUrl}
            alt="Template preview"
            className={autoAspect ? 'block w-full h-auto' : 'w-full h-full object-cover'}
          />
          
          {isRendering && (
            <div className="absolute top-2 right-2 bg-background/80 rounded-full p-2 z-50">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            </div>
          )}
        </div>
      ) : (
        <div className="relative w-full h-full">
          <div 
            className={useGradient ? 'h-full relative flex flex-col items-center justify-center cursor-pointer hover:opacity-90 transition-opacity' : 'h-4/5 relative flex flex-col items-center justify-center cursor-pointer hover:opacity-90 transition-opacity'}
            style={{ backgroundColor: imageUrl ? backgroundColor : 'transparent' }}
            onClick={onImageClick}
          >
            {imageUrl ? (
              <>
                {/* 1:1 с превью без шаблона: fill = cover по центру, fit = contain + размытые поля. */}
                {fitMode === 'fit' && (
                  <img
                    src={imageUrl}
                    alt=""
                    aria-hidden
                    className="w-full h-full absolute inset-0"
                    style={{ objectFit: 'cover', filter: 'blur(20px)', transform: 'scale(1.1)' }}
                  />
                )}
                <img
                  src={imageUrl}
                  alt="Preview"
                  className="w-full h-full absolute inset-0 z-10"
                  style={{ objectFit: fitMode === 'fit' ? 'contain' : 'cover' }}
                />
              </>
            ) : (
              <div className="flex flex-col items-center gap-3 z-10">
                <ImagePlus className="h-12 w-12 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Нажмите для загрузки фото
                </p>
              </div>
            )}
            
            {logoUrl && logoPosition !== 'bottom-left' && logoPosition !== 'bottom-right' && (
              <img 
                src={logoUrl} 
                alt="Logo preview"
                className={`absolute h-16 w-16 object-contain z-10 ${getLogoPositionClasses(logoPosition)}`}
              />
            )}
          </div>
          
          <div
            className={useGradient ? 'absolute bottom-0 left-0 right-0 h-1/5 flex items-center justify-between px-4 gap-3 z-20' : 'h-1/5 flex items-center justify-between px-4 gap-3 relative'}
            style={{ background: getBackgroundStyle() }}
          >
            {logoUrl && logoPosition === 'bottom-left' && (
              <img 
                src={logoUrl} 
                alt="Logo preview"
                className="h-12 w-12 object-contain flex-shrink-0"
              />
            )}
            
            <p 
              className="text-center flex-1"
              style={{ 
                color: textColor,
                fontFamily,
                fontSize: fontSize ? `${fontSize}px` : '20px',
                lineHeight: textLineSpacing && textLineSpacing > 0 ? textLineSpacing : 1.2,
                textTransform: textUppercase ? 'uppercase' : 'none',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                overflow: 'hidden',
                paddingTop: showLine && linePosition === 'top' ? `${lineHeight + 8}px` : '0',
                paddingBottom: showLine && linePosition === 'bottom' ? `${lineHeight + 8}px` : '0',
                fontWeight: fontBold ? 'bold' : 'normal',
                textShadow: [
                  textShadowEnabled
                    ? `${textShadowWidth/2}px ${textShadowWidth/2}px 0 ${textShadowColor}, -${textShadowWidth/2}px ${textShadowWidth/2}px 0 ${textShadowColor}, ${textShadowWidth/2}px -${textShadowWidth/2}px 0 ${textShadowColor}, -${textShadowWidth/2}px -${textShadowWidth/2}px 0 ${textShadowColor}, 0 0 ${textShadowBlur}px ${textShadowColor}`
                    : '',
                  textSoftShadowEnabled
                    ? `${textSoftShadowOffsetX}px ${textSoftShadowOffsetY}px ${textSoftShadowBlur}px ${colorWithOpacity(textSoftShadowColor, textSoftShadowOpacity)}`
                    : '',
                ].filter(Boolean).join(', ') || 'none',
              }}
            >
              {text}
            </p>
            
            {logoUrl && logoPosition === 'bottom-right' && (
              <img 
                src={logoUrl} 
                alt="Logo preview"
                className="h-12 w-12 object-contain flex-shrink-0"
              />
            )}
            
            {showLine && (
              <div 
                className={`absolute ${linePosition === 'top' ? 'top-0' : 'bottom-0'} left-0 right-0`}
                style={{ backgroundColor: lineColor, height: `${lineHeight}px` }}
              />
            )}
          </div>
          
          <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-50">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      )}
    </div>
  );
});

PostTemplate.displayName = 'PostTemplate';
